"use client"

import { useState, useEffect, useCallback } from "react"
import { Bell, X, Check, CheckCheck, MessageSquare, DollarSign, AlertCircle, Users, Briefcase, Clock, Calendar } from "lucide-react"
import { createBrowserClient } from "@/lib/supabase/client"
import { SwipeToDelete } from "@/components/swipe-to-delete"
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import Link from "next/link"
import { useNotificationSheet, NotificationSheetGrip } from "@/components/notifications-sheet"
import { useRouter, usePathname } from "next/navigation"
import { ReservationReviewModal } from "@/components/reservation-review-modal"
import { OwnerTeamApprovalCard } from "@/components/owner-team-approval-card"
import { useUserRole } from "@/providers/user-role-provider"
import { Lozenge } from "@/components/ui/lozenge"

type Notification = {
  id: string
  type: string
  title: string
  message: string
  metadata: {
    shop_id?: string
    shop_name?: string
    barber_id?: string
    barber_name?: string
    conversation_id?: string
    amount?: number
    sender_id?: string
    sender_name?: string
  }
  read: boolean
  created_at: string
}

type ReservationRequest = {
  id: string
  barber_id: string
  shop_id: string
  barber: {
    full_name: string
    email: string
    phone?: string
    avatar_url?: string
  }
  shop: {
    id: string
    name: string
  }
  rental_type: string
  requested_days: number[]
  start_date: string
  message: string
  status: string
  created_at: string
}

export function NotificationsPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const sheet = useNotificationSheet(isOpen)
  const [requests, setRequests] = useState<ReservationRequest[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<ReservationRequest | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all")
  const router = useRouter()
  const pathname = usePathname()

  // Remove one notification. RLS has allowed this since 016
  // ("Users can delete their own notifications", auth.uid() = user_id) — it
  // simply had no way to be triggered outside the full notifications page.
  // Optimistic: the row is already sliding out, so waiting on the round trip
  // would show it snap back for a frame. On failure it is put back.
  async function deleteNotification(id: string) {
    const previous = notifications
    setNotifications((prev) => prev.filter((n) => n.id !== id))
    const { error } = await createBrowserClient().from("notifications").delete().eq("id", id)
    if (error) {
      console.error("[notifications] delete failed:", error.message)
      setNotifications(previous)
    }
  }

  const unreadNotificationsCount = notifications.filter((n) => !n.read).length
  const totalUnreadCount = unreadNotificationsCount + requests.length

  // Route "View all" to the viewer's OWN notifications page. A client in Client
  // mode must land on their own page, not the owner's (which they can't access).
  const { isClient, role, actualUserType } = useUserRole()
  // Mode from context, not the URL. Reading the pathname meant that anywhere
  // outside /dashboard/barber — Hiring, Listings, Community — a barber-mode
  // user was treated as an owner, and their notifications routed to the owner
  // dashboard.
  const isBarberMode = (role ?? actualUserType) === "barber" || pathname?.includes("/dashboard/barber")
  const notificationsPath = isClient
    ? "/dashboard/client/notifications"
    : isBarberMode
      ? "/dashboard/barber/notifications"
      : "/dashboard/owner/notifications"

  // Close panel on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [isOpen])

  useEffect(() => {
    fetchNotifications()
    fetchRequests()

    const supabase = createBrowserClient()

    async function setupSubscriptions() {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return
      setUserId(user.id)

      const { data: userShops } = await supabase.from("shops").select("id").eq("owner_id", user.id)
      const shopIds = userShops?.map((s: { id: string }) => s.id) || []

      // Only subscribe to reservation_requests if the owner has shops
      let requestsChannel: ReturnType<typeof supabase.channel> | null = null
      if (shopIds.length > 0) {
        requestsChannel = supabase
          .channel("owner_requests_updates")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "reservation_requests",
              filter: `shop_id=in.(${shopIds.join(",")})`,
            },
            () => {
              fetchRequests()
            },
          )
          .subscribe()
      }

      const notificationsChannel = supabase
        .channel("owner_notifications_updates")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          (payload: RealtimePostgresChangesPayload<Notification>) => {
            if (payload.eventType === "INSERT") {
              setNotifications((prev) => [payload.new as Notification, ...prev])
            } else if (payload.eventType === "UPDATE") {
              setNotifications((prev) => prev.map((n) => (n.id === payload.new.id ? (payload.new as Notification) : n)))
            } else if (payload.eventType === "DELETE") {
              setNotifications((prev) => prev.filter((n) => n.id !== payload.old.id))
            }
          },
        )
        .subscribe()

      return () => {
        if (requestsChannel) supabase.removeChannel(requestsChannel)
        supabase.removeChannel(notificationsChannel)
      }
    }

    const cleanup = setupSubscriptions()

    return () => {
      cleanup.then((fn) => fn && fn())
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchRequests()
      fetchNotifications()
    }
  }, [isOpen])

  async function fetchNotifications() {
    const supabase = createBrowserClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return

    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .neq("type", "profile_view")
      .order("created_at", { ascending: false })
      .limit(50)

    if (data) {
      setNotifications(data)
    }
  }

  async function fetchRequests() {
    setIsLoading(true)
    const supabase = createBrowserClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      setIsLoading(false)
      return
    }

    const { data: userShops } = await supabase.from("shops").select("id").eq("owner_id", user.id)

    if (!userShops || userShops.length === 0) {
      setIsLoading(false)
      return
    }

    const shopIds = userShops.map((s: { id: string }) => s.id)

    const { data } = await supabase
      .from("reservation_requests")
      .select(`
        id,
        barber_id,
        shop_id,
        rental_type,
        requested_days,
        start_date,
        message,
        status,
        created_at,
        barber:profiles!reservation_requests_barber_id_fkey(full_name, avatar_url),
        shop:shops!reservation_requests_shop_id_fkey(id, name)
      `)
      .in("shop_id", shopIds)
      .eq("status", "pending")
      .order("created_at", { ascending: false })

    if (data) {
      setRequests(data as ReservationRequest[])
    }
    setIsLoading(false)
  }

  async function markAsRead(id: string) {
    const supabase = createBrowserClient()

    const { error } = await supabase
      .from("notifications")
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq("id", id)

    if (!error) {
      setNotifications(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)))
    }
  }

  async function markAllAsRead() {
    if (!userId) return
    const supabase = createBrowserClient()

    const { error } = await supabase
      .from("notifications")
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("read", false)

    if (!error) {
      setNotifications(notifications.map((n) => ({ ...n, read: true })))
    }
  }

  function handleNotificationClick(notification: Notification) {
    markAsRead(notification.id)

    // Types that carry no conversation and no shop used to do nothing at all
    // when tapped — appointment and profile-view notifications among them.
    const m = notification.metadata || {}
    const home = isBarberMode ? "/dashboard/barber" : "/dashboard/owner"

    const destination = (() => {
      if (m.conversation_id) return `/dashboard/messages?conversation=${m.conversation_id}`
      switch (notification.type) {
        case "session_booking":
        case "appointment_booked":
        case "appointment_cancelled":
        case "appointment_rescheduled":
          // An appointment is always about the user AS A BARBER. Without a
          // shop_id there is no owner-side page to send them to, so the
          // barber bookings hub is the only correct destination.
          if (isBarberMode || !m.shop_id) return "/dashboard/barber/bookings"
          return `/dashboard/owner/shop/${m.shop_id}/sessions`
        case "profile_view":
          return home
        case "job_inquiry":
          // An inquiry IS a message: job-inquire-modal sends it to the owner
          // first and only notifies afterwards. Both branches of the ternary
          // that used to live here returned "/hiring" — the job post, not the
          // conversation — so tapping "someone asked about your post" took you
          // to your own listing with no way to see what they said.
          // barber_id is on the metadata the modal writes.
          return m.barber_id
            ? `${isBarberMode ? "/dashboard/messages" : "/dashboard/owner/messages"}?userId=${m.barber_id}`
            : isBarberMode
              ? "/dashboard/messages"
              : "/dashboard/owner/messages"
        default:
          if (!m.shop_id) return home
          return isBarberMode ? `/listings/${m.shop_id}` : `/dashboard/owner/shop/${m.shop_id}`
      }
    })()

    if (destination) {
      setIsOpen(false)
      router.push(destination)
    }
  }

  function handleModalAction(requestId: string) {
    setRequests(requests.filter((r) => r.id !== requestId))
    setIsModalOpen(false)
    setSelectedRequest(null)
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "payment_received":
      case "payment_reminder":
        return <DollarSign className="h-4 w-4" />
      case "new_message":
        return <MessageSquare className="h-4 w-4" />
      case "invitation_accepted":
      case "reservation_approved":
        return <Check className="h-4 w-4" />
      case "invitation_declined":
      case "reservation_rejected":
        return <X className="h-4 w-4" />
      case "new_barber":
        return <Users className="h-4 w-4" />
      case "plan_offer":
      case "new_plan_offer":
        return <Briefcase className="h-4 w-4" />
      case "session_booking":
      case "renewal_reminder":
        return <Calendar className="h-4 w-4" />
      default:
        return <Bell className="h-4 w-4" />
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    const diffInHours = Math.floor(diffInMinutes / 60)
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInMinutes < 1) return "Just now"
    if (diffInMinutes < 60) return `${diffInMinutes}m`
    if (diffInHours < 24) return `${diffInHours}h`
    if (diffInDays < 7) return `${diffInDays}d`
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }

  const filteredNotifications = activeTab === "unread"
    ? notifications.filter(n => !n.read)
    : notifications

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = new Date(notification.created_at)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    let key: string
    if (date.toDateString() === today.toDateString()) {
      key = "Today"
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = "Yesterday"
    } else {
      key = date.toLocaleDateString("en-US", { month: "long", day: "numeric" })
    }

    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(notification)
    return groups
  }, {} as Record<string, Notification[]>)

  return (
    <>
      {/* Bell Icon Button — softer active scale (was scale-90, felt
          jarring) and longer transition so the press feels intentional. */}
      <button
        onClick={() => setIsOpen(true)}
        className="relative rounded-full p-2 text-foreground hover:bg-muted/80 active:scale-95 transition-all duration-200"
        aria-label="Open notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={1.75} />
        {totalUnreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums leading-none tracking-tight flex items-center justify-center px-1 ring-2 ring-background">
            {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
          </span>
        )}
      </button>

      {/* Backdrop — subtle dim on both mobile and desktop so the panel
          reads as a foreground layer regardless of breakpoint. Mobile
          gets a lighter overlay than desktop since the panel itself
          covers more of the viewport on mobile. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] transition-opacity duration-500 ease-out bg-black/10 sm:bg-black/20 backdrop-blur-sm"
          // The launcher fades while the sheet is open (FloatingNav watches
          // this attribute) — it was sitting under the sheet and grabbing taps.
          data-floating-nav="hide"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Notifications panel.
          Mobile (< sm): drops from the top but stops 8rem above the
          bottom so the FloatingNav launcher remains visible and
          tappable below the sheet — users can quick-glance and
          dismiss / hop to nav without dragging the sheet closed
          first. Rounded-b-[2.5rem] reads it as a pulled-down sheet.
          Desktop (sm+): slides in from the right, full height,
          shadow-2xl tray. 280ms ease for a deliberate slide. */}
      <div
        className={`notif-sheet fixed bg-background z-[60] flex flex-col overflow-hidden transform transition-[transform,opacity] duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform
          top-0 left-0 right-0
          sm:left-auto sm:right-0 sm:h-full sm:w-[400px] sm:shadow-2xl
          ${isOpen
            ? "translate-x-0 translate-y-0 opacity-100"
            : "-translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0"}
        `}
        // Phone: height comes from the sheet hook (grip-resizable); sm+ keeps
        // the full-height tray. Radius lives in .notif-sheet (globals.css).
        style={{ height: sheet.height }}
      >
        {/* Panel Header */}
        <div className="flex-shrink-0 bg-background z-10 border-b border-border">
          <div className="notif-sheet-header flex items-center justify-between px-5 pb-4">
            <div>
              <h2 className="text-[22px] font-bold tracking-tight text-foreground">Notifications</h2>
              {totalUnreadCount > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {totalUnreadCount} unread
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadNotificationsCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Close notifications"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Tabs — project segmented-pill pattern (matches /listings
              density toggle and /community view tabs). Cleaner than
              the dated tab-with-underline approach we had before. */}
          <div className="px-5 pb-3">
            <div className="inline-flex items-center gap-1 p-0.5 rounded-full bg-muted">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-3.5 py-1 text-xs font-medium rounded-full transition-all ${
                  activeTab === "all"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab("unread")}
                className={`px-3.5 py-1 text-xs font-medium rounded-full transition-all inline-flex items-center gap-1.5 ${
                  activeTab === "unread"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Unread
                {unreadNotificationsCount > 0 && (
                  <span className="min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums leading-none tracking-tight flex items-center justify-center px-1">
                    {unreadNotificationsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
            </div>
          ) : requests.length === 0 && filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Bell className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium text-foreground mb-1">
                {activeTab === "unread" ? "All caught up" : "No notifications yet"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-[260px]">
                {activeTab === "unread"
                  ? "You've read all your notifications"
                  : "When you receive notifications, they'll appear here"}
              </p>
            </div>
          ) : (
            <div>
              {/* Pending Requests Section */}
              {requests.length > 0 && activeTab === "all" && (
                /* Ink, not amber. The strip was a caution colour on a cream
                   wash with an emerald button beside it — three palettes in
                   one row, none of them BOOTH's, and amber reading as a
                   warning when a booth request is good news. Black strip,
                   plain rows under it, so the only thing drawing the eye is
                   the thing you are meant to act on. */
                <div className="border-b border-border/50">
                  <div className="bg-booth-ink px-5 py-2.5">
                    <div className="flex items-center gap-2 text-white">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider">
                        Pending approval ({requests.length})
                      </span>
                    </div>
                  </div>
                  <div className="divide-y divide-border/50">
                    {requests.slice(0, 3).map((request) => (
                      <div key={request.id} className="p-4">
                        <OwnerTeamApprovalCard
                          requestId={request.id}
                          barberId={request.barber_id}
                          barberName={request.barber?.full_name || "Unknown Barber"}
                          barberAvatar={request.barber?.avatar_url}
                          shopId={request.shop_id}
                          shopName={request.shop?.name || "Unknown Shop"}
                          rentalType={request.rental_type}
                          onApproved={() => {
                            setRequests(requests.filter((r) => r.id !== request.id))
                          }}
                          onRejected={() => {
                            setRequests(requests.filter((r) => r.id !== request.id))
                          }}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Grouped Notifications */}
              {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
                <div key={date}>
                  <div className="sticky top-0 px-5 py-2 bg-muted/30 backdrop-blur-sm">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {date}
                    </span>
                  </div>
                  <div className="divide-y divide-border/30">
                    {dateNotifications.map((notification) => (
                      <SwipeToDelete
                        key={notification.id}
                        onDelete={() => deleteNotification(notification.id)}
                        label="Delete notification"
                      >
                      <div
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-5 py-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                          !notification.read ? "bg-muted/50 dark:bg-white/[0.03]" : ""
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`h-9 w-9 flex items-center justify-center flex-shrink-0 ${
                            !notification.read ? "text-foreground" : "text-muted-foreground"
                          }`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className={`text-sm leading-snug ${
                                !notification.read ? "font-semibold text-foreground" : "font-medium text-foreground/80"
                              }`}>
                                {notification.title}
                              </h3>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs text-muted-foreground">
                                  {formatTimestamp(notification.created_at)}
                                </span>
                                {!notification.read && (
                                  <Lozenge color="#3b82f6" />
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                        </div>
                      </div>
                      </SwipeToDelete>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel Footer */}
        <div className="flex-shrink-0 border-t border-border/50 bg-background">
          <Link
            href={notificationsPath}
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-2 py-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            View all notifications
          </Link>
          <NotificationSheetGrip {...sheet.gripProps} />
        </div>
      </div>

      {selectedRequest && (
        <ReservationReviewModal
          request={selectedRequest}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedRequest(null)
          }}
          onAction={handleModalAction}
        />
      )}
    </>
  )
}
